/**
 * useContainerState Hook
 *
 * Manages the hierarchical container items structure for the sidebar.
 *
 * Three-phase approach to eliminate loading flash:
 * 1. SYNC: Build initial structure from spaces data during render (before paint)
 * 1.5. SYNC ITEMS: Inject items from spaceItems when they arrive (before paint)
 * 2. ASYNC: Enhance with nested sub-folders and folder-level items from Supabase
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import type { Space } from '@/types';
import type { SpaceItems } from '@/store/appStore';
import type { ContainerState, FolderData, ListData, FolderMapping } from './types';
import type { SpaceRecord } from '@/db/types';
import type { Folder, ListItem } from '@/types';
import { getSpaceChildren } from '@/db/queries';
import { useAppStore } from '@/store/appStore';
import { handleError } from '@/utils/errorHandler';

export function useContainerState(
  spaces: Space[],
  folderMappings: Record<string, FolderMapping> | undefined,
  spaceItemsMap: Record<string, SpaceItems> | undefined
): ContainerState & {
  setContainerItems: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setFolderDataMap: React.Dispatch<React.SetStateAction<Record<string, FolderData>>>;
  setListDataMap: React.Dispatch<React.SetStateAction<Record<string, ListData>>>;
} {
  const [containerItems, setContainerItems] = useState<Record<string, string[]>>({});
  const [folderDataMap, setFolderDataMap] = useState<Record<string, FolderData>>({});
  const [listDataMap, setListDataMap] = useState<Record<string, ListData>>({});

  const editingItems = useAppStore((state) => state.editingItems);
  const projectItems = useAppStore((state) => state.projectItems);

  // Structural key: only changes when space IDs change, not on task count updates
  const spacesStructuralKey = useMemo(
    () => spaces.map(s => s.id).sort().join(','),
    [spaces],
  );

  // Key for space items: changes when items are loaded/updated
  const spaceItemsKey = useMemo(() => {
    if (!spaceItemsMap) return '';
    const entries = Object.entries(spaceItemsMap);
    if (entries.length === 0) return '';
    return entries
      .map(([name, items]) => `${name}:${items.length}`)
      .sort()
      .join(',');
  }, [spaceItemsMap]);

  // Keep a ref to spaces so the async effect can read the latest data
  const spacesRef = useRef(spaces);
  spacesRef.current = spaces;

  // ============================================================
  // PHASE 1: Synchronous initialization from spaces data
  // Uses "set state during render" pattern to avoid flash.
  // React aborts the current render and retries with the new state
  // before committing to the DOM, so the user never sees empty content.
  // Also includes items from spaceItemsMap if already available.
  // ============================================================
  const [prevStructuralKey, setPrevStructuralKey] = useState('');
  const [prevSpaceItemsKey, setPrevSpaceItemsKey] = useState('');

  if (spacesStructuralKey !== prevStructuralKey && spaces.length > 0) {
    setPrevStructuralKey(spacesStructuralKey);
    // Track items key too so Phase 1.5 doesn't re-inject
    if (spaceItemsKey) setPrevSpaceItemsKey(spaceItemsKey);

    const syncCI: Record<string, string[]> = {};
    const syncFDM: Record<string, FolderData> = {};
    const syncLDM: Record<string, ListData> = {};

    for (const space of spaces) {
      const items: string[] = [];

      for (const folder of space.folders) {
        if (folder.id) {
          items.push(`folder-${folder.id}`);
          syncFDM[folder.id] = { name: folder.name, spaceId: space.id };
          const folderItems: string[] = [];
          for (const list of folder.lists) {
            folderItems.push(`list-${list.id}`);
            syncLDM[list.id] = {
              name: list.name,
              spaceId: space.id,
              folderId: folder.id,
              taskCount: list.taskCount,
            };
          }
          syncCI[folder.id] = folderItems;
        }
      }

      // Root lists
      if (space.rootLists) {
        for (const list of space.rootLists) {
          items.push(`list-${list.id}`);
          syncLDM[list.id] = {
            name: list.name,
            spaceId: space.id,
            folderId: null,
            taskCount: list.taskCount,
          };
        }
      }

      // Root items (documents, chats, etc.) from spaceItemsMap if available
      const spaceItemsList = spaceItemsMap?.[space.name];
      if (spaceItemsList) {
        for (const item of spaceItemsList) {
          items.push(`item-${item.id}`);
        }
      }

      syncCI[space.id] = items;
    }

    setContainerItems(syncCI);
    setFolderDataMap(syncFDM);
    setListDataMap(syncLDM);
  }

  // ============================================================
  // PHASE 1.5: Sync injection of items when spaceItemsMap arrives
  // after Phase 1. Uses same "set state during render" pattern.
  // This handles the case where items load from cache after
  // the initial structure is already built.
  // ============================================================
  if (
    spaceItemsKey !== prevSpaceItemsKey &&
    spaceItemsKey !== '' &&
    prevStructuralKey !== '' &&
    spaces.length > 0
  ) {
    setPrevSpaceItemsKey(spaceItemsKey);

    setContainerItems(prev => {
      let changed = false;
      const updated = { ...prev };

      for (const space of spaces) {
        const items = spaceItemsMap?.[space.name];
        if (!items || items.length === 0) continue;

        const current = updated[space.id] || [];
        const currentSet = new Set(current);
        const newItemIds: string[] = [];

        for (const item of items) {
          const prefixed = `item-${item.id}`;
          if (!currentSet.has(prefixed)) {
            newItemIds.push(prefixed);
          }
        }

        if (newItemIds.length > 0) {
          updated[space.id] = [...current, ...newItemIds];
          changed = true;
        }
      }

      return changed ? updated : prev;
    });
  }

  // ============================================================
  // PHASE 2: Async enhancement for non-structural items
  // Fetches documents, chats, voice channels, etc. from Supabase
  // and handles nested sub-folders not captured in spaces data.
  // Uses spacesStructuralKey so it only re-runs on structural changes,
  // not on every task count update.
  // ============================================================
  useEffect(() => {
    if (spaces.length === 0) return;

    let cancelled = false;

    const enhanceWithAsyncData = async () => {
      const currentSpaces = spacesRef.current;
      const newContainerItems: Record<string, string[]> = {};
      const newFolderDataMap: Record<string, FolderData> = {};
      const newListDataMap: Record<string, ListData> = {};

      const buildFolderItems = async (folderId: string, space: Space): Promise<string[]> => {
        const folderItems: string[] = [];

        if (folderId.startsWith('temp-')) {
          return folderItems;
        }

        try {
          const children = await getSpaceChildren(folderId);
          if (cancelled) return folderItems;

          const folders: SpaceRecord[] = [];
          const lists: SpaceRecord[] = [];
          const items: SpaceRecord[] = [];

          for (const child of children) {
            if (child.type === 'folder') {
              folders.push(child);
            } else if (child.type === 'list') {
              lists.push(child);
            } else if (['document', 'chat', 'voice_channel', 'spreadsheet', 'presentation'].includes(child.type)) {
              items.push(child);
            }
          }

          for (const subFolder of folders) {
            const subFolderId = subFolder.id;
            folderItems.push(`folder-${subFolderId}`);
            newFolderDataMap[subFolderId] = { name: subFolder.name, spaceId: space.id };
            newContainerItems[subFolderId] = await buildFolderItems(subFolderId, space);
            if (cancelled) return folderItems;
          }

          for (const list of lists) {
            folderItems.push(`list-${list.id}`);
            let taskCount = 0;
            for (const folder of (space.folders || [])) {
              const foundList = folder.lists?.find((l: ListItem) => l.id === list.id);
              if (foundList) {
                taskCount = foundList.taskCount || 0;
                break;
              }
            }
            newListDataMap[list.id] = { name: list.name, spaceId: space.id, folderId: folderId, taskCount };
          }

          for (const item of items) {
            folderItems.push(`item-${item.id}`);
          }
        } catch (error) {
          handleError(error, { level: 'warning', message: 'Errore nel caricamento dei contenuti della cartella' });
        }

        return folderItems;
      };

      for (const space of currentSpaces) {
        if (cancelled) return;
        const items: string[] = [];
        const spaceId = space.id;

        if (!spaceId.startsWith('temp-')) {
          try {
            const spaceChildren = await getSpaceChildren(spaceId);
            if (cancelled) return;

            // Process folders
            for (const folder of spaceChildren.filter(c => c.type === 'folder')) {
              const folderId = folder.id;
              items.push(`folder-${folderId}`);
              newFolderDataMap[folderId] = { name: folder.name, spaceId: space.id };
              newContainerItems[folderId] = await buildFolderItems(folderId, space);
              if (cancelled) return;
            }

            // Process root lists
            for (const list of spaceChildren.filter(c => c.type === 'list')) {
              items.push(`list-${list.id}`);
              let taskCount = 0;
              // Check root lists first
              const rootList = space.rootLists?.find(l => l.id === list.id);
              if (rootList) {
                taskCount = rootList.taskCount || 0;
              } else {
                for (const folder of (space.folders || [])) {
                  const foundList = folder.lists?.find((l: ListItem) => l.id === list.id);
                  if (foundList) {
                    taskCount = foundList.taskCount || 0;
                    break;
                  }
                }
              }
              newListDataMap[list.id] = { name: list.name, spaceId: space.id, folderId: null, taskCount };
            }

            // Process root items (documents, chats, etc.)
            for (const item of spaceChildren.filter(c => ['document', 'chat', 'voice_channel', 'spreadsheet', 'presentation'].includes(c.type))) {
              items.push(`item-${item.id}`);
            }
          } catch (error) {
            handleError(error, { level: 'warning', message: 'Errore nel caricamento dei contenuti dello spazio' });
          }
        }

        newContainerItems[spaceId] = items;

        // Handle temp items from editingItems
        const tempItemsForSpace: string[] = [];
        const pendingCreations = useAppStore.getState().pendingItemCreations;

        editingItems.forEach((itemId) => {
          if (!itemId.startsWith('temp-')) return;

          const creationInfo = pendingCreations[itemId];
          if (!creationInfo) return;

          if (creationInfo.spaceName !== space.name) return;

          if (creationInfo.containerId === spaceId) {
            if (creationInfo.itemType === 'folder') {
              tempItemsForSpace.push(`folder-${itemId}`);
              newFolderDataMap[itemId] = { name: '', spaceId: space.id };
              newContainerItems[itemId] = [];
            } else if (creationInfo.itemType === 'list') {
              tempItemsForSpace.push(`list-${itemId}`);
              newListDataMap[itemId] = {
                name: '',
                spaceId: space.id,
                folderId: null,
                taskCount: 0,
              };
            } else {
              tempItemsForSpace.push(`item-${itemId}`);
            }
          }

          space.folders.forEach((folder: Folder) => {
            if (!folder.id) return;
            if (creationInfo.containerId === folder.id) {
              if (!newContainerItems[folder.id]) {
                newContainerItems[folder.id] = [];
              }

              if (creationInfo.itemType === 'folder') {
                if (!newContainerItems[folder.id].includes(`folder-${itemId}`)) {
                  newContainerItems[folder.id].unshift(`folder-${itemId}`);
                }
                newFolderDataMap[itemId] = { name: '', spaceId: space.id };
                newContainerItems[itemId] = [];
              } else if (creationInfo.itemType === 'list') {
                if (!newContainerItems[folder.id].includes(`list-${itemId}`)) {
                  newContainerItems[folder.id].unshift(`list-${itemId}`);
                }
                newListDataMap[itemId] = {
                  name: '',
                  spaceId: space.id,
                  folderId: folder.id,
                  taskCount: 0,
                };
              } else {
                if (!newContainerItems[folder.id].includes(`item-${itemId}`)) {
                  newContainerItems[folder.id].push(`item-${itemId}`);
                }
              }
            }
          });
        });

        if (tempItemsForSpace.length > 0) {
          newContainerItems[spaceId] = [...tempItemsForSpace, ...items];
        }
      }

      if (!cancelled) {
        // MERGE: preserve Phase 1 ordering for existing items,
        // only update when items are structurally added/removed
        setContainerItems(prev => {
          let hasStructuralChange = false;
          const merged: Record<string, string[]> = {};
          const allKeys = new Set([...Object.keys(prev), ...Object.keys(newContainerItems)]);

          for (const key of allKeys) {
            const p = prev[key];
            const n = newContainerItems[key];

            if (!p && n) {
              // New container added by Phase 2
              merged[key] = n;
              hasStructuralChange = true;
            } else if (p && !n) {
              // Container removed — drop it
              hasStructuralChange = true;
            } else if (p && n) {
              const pSet = new Set(p);
              const nSet = new Set(n);
              const sameSet = p.length === n.length && p.every(item => nSet.has(item));

              if (sameSet) {
                // Same items — keep Phase 1 order
                merged[key] = p;
              } else {
                // Items added or removed: keep existing order, append new ones
                const kept = p.filter(item => nSet.has(item));
                const additions = n.filter(item => !pSet.has(item));
                merged[key] = [...kept, ...additions];
                hasStructuralChange = true;
              }
            }
          }

          return hasStructuralChange ? merged : prev;
        });
        setFolderDataMap(prev => {
          const newKeys = Object.keys(newFolderDataMap);
          if (Object.keys(prev).length !== newKeys.length) return newFolderDataMap;
          for (const key of newKeys) {
            if (!prev[key] || prev[key].name !== newFolderDataMap[key].name) return newFolderDataMap;
          }
          return prev;
        });
        setListDataMap(prev => {
          const newKeys = Object.keys(newListDataMap);
          if (Object.keys(prev).length !== newKeys.length) return newListDataMap;
          for (const key of newKeys) {
            if (!prev[key] || prev[key].name !== newListDataMap[key].name) return newListDataMap;
          }
          return prev;
        });
      }
    };

    enhanceWithAsyncData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Use structural key to avoid re-querying on task count changes
  }, [spacesStructuralKey, folderMappings, editingItems, projectItems]);

  // ============================================================
  // Lightweight sync: update task counts in listDataMap when
  // spaces change (e.g. from recomputeTaskCounts)
  // ============================================================
  useEffect(() => {
    if (spaces.length === 0) return;

    setListDataMap(prev => {
      let changed = false;
      const updated = { ...prev };

      for (const space of spaces) {
        for (const folder of space.folders) {
          for (const list of folder.lists) {
            if (updated[list.id] && updated[list.id].taskCount !== list.taskCount) {
              updated[list.id] = { ...updated[list.id], taskCount: list.taskCount };
              changed = true;
            }
          }
        }
        for (const list of (space.rootLists || [])) {
          if (updated[list.id] && updated[list.id].taskCount !== list.taskCount) {
            updated[list.id] = { ...updated[list.id], taskCount: list.taskCount };
            changed = true;
          }
        }
      }

      return changed ? updated : prev;
    });
  }, [spaces]);

  return {
    containerItems,
    folderDataMap,
    listDataMap,
    setContainerItems,
    setFolderDataMap,
    setListDataMap,
  };
}
